import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';

import Banner2 from '../../assets/img/website-images/Blogdetailsbanner2.png';

/* ─── Variants ─────────────────────────────────────────────────── */
const heroVariants = {
    hidden: { opacity: 0, scale: 1.04 },
    visible: {
        opacity: 1, scale: 1,
        transition: { duration: 1.0, ease: [0.22, 1, 0.36, 1] },
    },
};

const titleVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1, y: 0,
        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
    },
};

/* ─── Paragraph with individual scroll trigger ──────────────────── */
function AnimPara({ children }) {
    const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
    return (
        <motion.p
            ref={ref}
            initial={{ opacity: 0, y: 28 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, ease: 'easeOut' }}
        >
            {children}
        </motion.p>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function BlogTwo() {
    const { ref: heroRef, inView: heroInView } = useInView({ threshold: 0.2, triggerOnce: true });
    const { ref: titleRef, inView: titleInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: hero image clip-path wipe */
    const imgWrapRef = useRef(null);
    useEffect(() => {
        if (heroInView && imgWrapRef.current) {
            gsap.fromTo(
                imgWrapRef.current,
                { clipPath: 'inset(0 100% 0 0)', opacity: 1 },
                { clipPath: 'inset(0 0% 0 0)', duration: 1.1, ease: 'power3.inOut', delay: 0.1 }
            );
        }
    }, [heroInView]);

    /* GSAP: hero image scroll parallax */
    const heroImgRef = useRef(null);
    useEffect(() => {
        const handleScroll = () => {
            if (!heroImgRef.current) return;
            const rect = heroImgRef.current.getBoundingClientRect();
            const progress = 1 - (rect.bottom / (window.innerHeight + rect.height));
            gsap.set(heroImgRef.current, { y: progress * 30 });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    /* GSAP: reading progress bar */
    const progressRef = useRef(null);
    useEffect(() => {
        const handleScroll = () => {
            if (!progressRef.current) return;
            const scrollTop = window.scrollY;
            const docHeight = document.body.scrollHeight - window.innerHeight;
            const progress  = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
            gsap.set(progressRef.current, { width: `${progress}%` });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <>
            {/* Reading progress bar */}
            <div style={{
                position: 'fixed', top: 0, left: 0, height: '3px',
                background: 'transparent', zIndex: 9999, width: '100%',
                pointerEvents: 'none',
            }}>
                <div
                    ref={progressRef}
                    style={{
                        height: '100%', width: '0%',
                        background: 'linear-gradient(90deg, #1E5AA8, #F27024)',
                        borderRadius: '0 2px 2px 0',
                    }}
                />
            </div>

            <section className="project-detail">
                <div className="container">

                    {/* ── Hero Image ── */}
                    <div className="upper-box" ref={heroRef}>
                        <motion.div
                            variants={heroVariants}
                            initial="hidden"
                            animate={heroInView ? 'visible' : 'hidden'}
                            style={{ borderRadius: '12px', overflow: 'hidden' }}
                        >
                            <div ref={imgWrapRef} style={{ overflow: 'hidden', borderRadius: '12px' }}>
                                <figure className="image" style={{ margin: 0, overflow: 'hidden' }}>
                                    <img
                                        ref={heroImgRef}
                                        src={Banner2}
                                        alt="Blog Banner"
                                        style={{ display: 'block', width: '100%', transformOrigin: 'center' }}
                                    />
                                </figure>
                            </div>
                        </motion.div>
                    </div>

                    {/* ── Article Content ── */}
                    <div className="lower-content2">
                        <div className="row">
                            <div className="text-column col-lg-12 col-md-12 col-sm-12">
                                <div className="s-about-content">

                                    {/* Title */}
                                    <motion.h2
                                        ref={titleRef}
                                        variants={titleVariants}
                                        initial="hidden"
                                        animate={titleInView ? 'visible' : 'hidden'}
                                    >
                                        Why Practical Training Matters More Than Ever in Pharmacy Education
                                    </motion.h2>

                                    <AnimPara>
                                        Pharmacy education has evolved significantly over the past decade. While a strong
                                        theoretical foundation in subjects like Pharmacology, Medicinal Chemistry, and
                                        Pharmaceutics remains essential, the modern pharmaceutical industry demands something
                                        more — graduates who can apply their knowledge in real, complex environments from
                                        day one. This is why practical training has become the cornerstone of quality
                                        pharmacy education.
                                    </AnimPara>

                                    <AnimPara>
                                        In a traditional classroom, students learn about drug formulation, stability testing,
                                        and dosage calculations. But it is only in the laboratory that these concepts truly
                                        come alive. Hands-on training teaches students precision — how to measure, mix,
                                        observe, and record with the level of accuracy that pharmaceutical work demands.
                                        A small error in a laboratory setting becomes a valuable lesson; the same error in
                                        industry could have serious consequences. Practical training builds the instincts
                                        to get it right.
                                    </AnimPara>

                                    <AnimPara>
                                        At Akash Institute of Pharmacy, we have invested in <strong>state-of-the-art laboratories</strong>
                                        for Pharmaceutics, Pharmacology, Pharmaceutical Chemistry, and Pharmacognosy.
                                        These labs are not just teaching spaces — they are environments designed to
                                        simulate real industry conditions. Students work with advanced equipment,
                                        follow standard operating procedures, and complete experiments that mirror
                                        what they will encounter in their professional careers.
                                    </AnimPara>

                                    <AnimPara>
                                        Practical training also develops <strong>soft skills</strong> that no textbook can teach.
                                        Working in a laboratory requires teamwork, time management, attention to detail,
                                        and the ability to handle pressure. Students learn to communicate their findings
                                        clearly, troubleshoot when experiments do not go as planned, and develop the
                                        scientific mindset needed to be a researcher or industry professional.
                                    </AnimPara>

                                    <AnimPara>
                                        The healthcare and pharmaceutical industries are increasingly favoring candidates
                                        who arrive job-ready. Employers value graduates who have spent meaningful time
                                        in labs, understand processes end-to-end, and can contribute from their very
                                        first week on the job. At AIP, our commitment to hands-on learning ensures
                                        our graduates are exactly that — confident, capable, and career-ready.
                                    </AnimPara>

                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </section>
        </>
    );
}

export default BlogTwo;
